import React, { useState } from "react";

import { css, cx } from "@emotion/css";
import { useSearch } from "@tanstack/react-router";

import { FontSizes } from "@/assets/fonts.ts";
import { useSearchPatientsList } from "@/hooks/usePatientList.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { BackToTop } from "@/uiKit/molecules/BackToTop.tsx";
import { NurseInclusionHistoryModal } from "@/uiKit/organisms/NurseInclusionHistoryModal.tsx";
import { NurseInclusionCreationSideSheet } from "@/uiKit/organisms/PatientCreationAndRenewal/NurseInclusionCreationSideSheet.tsx";
import { NursePreinclusionActions } from "@/uiKit/organisms/PatientsList/NursePreinclusionActions.tsx";
import { PatientListMenu } from "@/uiKit/organisms/PatientsList/PatientListMenu.tsx";
import { PatientListSearch } from "@/uiKit/organisms/PatientsList/PatientListSearch.tsx";

import { PatientListTable } from "./PatientListTable.tsx";

export type NursePatientListCardProps = {
  className?: string;
};

export const NursePatientListCard: React.FC<NursePatientListCardProps> = ({ className }) => {
  const styles = useStyles(makeStyles);
  const [openNurseInclusion, setOpenNurseInclusion] = useState<boolean>(false);
  const [openNurseInclusionHistory, setOpenNurseInclusionHistory] = useState<boolean>(false);
  const searchParams = useSearch({
    from: "/_practitioner/patients/",
  });
  const urlSelectedTab = searchParams.menu ?? "monitored";
  const data = Queries.practitioner.useNursePatientList({});
  const patientsListData = useSearchPatientsList(data);
  const hasMultipleDoctors = new Set(data.all.map(patient => patient.doctor?.id)).size > 1;

  return (
    <Card id="nurse-patients-list-card" className={cx(styles.container, className)}>
      <CardHeader
        className={styles.header}
        variant="plain"
        title={
          <>
            <PatientListMenu className={styles.menu} selectedItem={urlSelectedTab} patientListData={patientsListData} />
            <PatientListSearch />
          </>
        }
        action={
          <NursePreinclusionActions
            onClickReinclusion={() => setOpenNurseInclusion(true)}
            onClickHistory={() => setOpenNurseInclusionHistory(true)}
          />
        }
      />
      <CardContent className={styles.content}>
        <PatientListTable
          id="nurse-patient-list-table"
          qualification="nurse"
          nurseHasMultipleDoctors={hasMultipleDoctors}
          tab={urlSelectedTab}
          data={patientsListData[urlSelectedTab]}
        />
        <BackToTop id="back-to-top-of-nurse-patient-list" threshold={100} />
        <NurseInclusionHistoryModal
          open={openNurseInclusionHistory}
          onClose={() => setOpenNurseInclusionHistory(false)}
        />
        <NurseInclusionCreationSideSheet
          id="nurse-inclusion-creation-form"
          open={openNurseInclusion}
          onClose={() => setOpenNurseInclusion(false)}
          onSubmit={() => {
            setOpenNurseInclusion(false);
          }}
        />
      </CardContent>
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "column",
  }),
  header: css({
    flexWrap: "wrap",
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
    position: "sticky",
    top: 0,
    zIndex: 1,
  }),
  menu: css({}),
  loader: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  content: css({
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: "1 1",
  }),
  icons: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
